import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { BasicsStep } from './wizard/BasicsStep'
import { ScopeStep } from './wizard/ScopeStep'
import { ConditionStep } from './wizard/ConditionStep'
import { ActionsStep } from './wizard/ActionsStep'
import { ReviewStep } from './wizard/ReviewStep'
import type { AlertRule, AlertScopeSelector, AlertCondition, AlertChannelConfig } from '@/types/fleetvision'

interface AlertWizardProps {
  open: boolean
  onClose: () => void
  editingRule?: AlertRule | null
}

interface WizardState {
  // Basics
  name: string
  description: string
  severity: 'critical' | 'warning' | 'info'
  enabled: boolean

  // Scope
  scope_type: 'fleet' | 'vehicle' | 'driver' | 'geofence' | 'route'
  scope_selector: AlertScopeSelector

  // Condition
  condition: AlertCondition | null

  // Actions
  channels: AlertChannelConfig[]

  // Evaluation settings
  evaluation_window_sec: number
  frequency_sec: number
  fire_mode: 'on_change' | 'every_time_window'
  suppression_window_sec: number
}

const STEPS = [
  { id: 1, title: 'Basics', description: 'Name and severity' },
  { id: 2, title: 'Scope', description: 'What to monitor' },
  { id: 3, title: 'Condition', description: 'When to alert' },
  { id: 4, title: 'Actions', description: 'How to notify' },
  { id: 5, title: 'Review', description: 'Confirm and create' },
]

export function AlertWizard({ open, onClose, editingRule }: AlertWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [state, setState] = useState<WizardState>({
    name: '',
    description: '',
    severity: 'warning',
    enabled: true,
    scope_type: 'fleet',
    scope_selector: {},
    condition: null,
    channels: [],
    evaluation_window_sec: 300,
    frequency_sec: 60,
    fire_mode: 'on_change',
    suppression_window_sec: 300,
  })

  useEffect(() => {
    if (editingRule) {
      setState({
        name: editingRule.name,
        description: editingRule.description || '',
        severity: editingRule.severity,
        enabled: editingRule.enabled,
        scope_type: editingRule.scope_type,
        scope_selector: editingRule.scope_selector,
        condition: editingRule.condition,
        channels: editingRule.channels,
        evaluation_window_sec: editingRule.evaluation_window_sec,
        frequency_sec: editingRule.frequency_sec,
        fire_mode: editingRule.fire_mode,
        suppression_window_sec: editingRule.suppression_window_sec || 300,
      })
    } else {
      // Reset to defaults when creating new
      setState({
        name: '',
        description: '',
        severity: 'warning',
        enabled: true,
        scope_type: 'fleet',
        scope_selector: {},
        condition: null,
        channels: [],
        evaluation_window_sec: 300,
        frequency_sec: 60,
        fire_mode: 'on_change',
        suppression_window_sec: 300,
      })
    }
    setCurrentStep(1)
  }, [editingRule, open])

  const updateState = (updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1: // Basics
        return state.name.trim().length > 0
      case 2: // Scope
        return true // Scope is always valid (fleet is default)
      case 3: // Condition
        return state.condition !== null
      case 4: // Actions
        return state.channels.length > 0
      case 5: // Review
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        name: state.name,
        description: state.description,
        severity: state.severity,
        enabled: state.enabled,
        scope_type: state.scope_type,
        scope_selector: state.scope_selector,
        condition: state.condition,
        channels: state.channels,
        evaluation_window_sec: state.evaluation_window_sec,
        frequency_sec: state.frequency_sec,
        fire_mode: state.fire_mode,
        suppression_window_sec: state.suppression_window_sec,
      }

      const url = editingRule
        ? `/api/alert-rules/${editingRule.id}`
        : '/api/alert-rules'
      const method = editingRule ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (data.success || data.id) {
        onClose()
      } else {
        alert('Failed to save alert rule')
      }
    } catch (error) {
      console.error('Error saving alert rule:', error)
      alert('Failed to save alert rule')
    } finally {
      setSaving(false)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BasicsStep state={state} updateState={updateState} />
      case 2:
        return <ScopeStep state={state} updateState={updateState} />
      case 3:
        return <ConditionStep state={state} updateState={updateState} />
      case 4:
        return <ActionsStep state={state} updateState={updateState} />
      case 5:
        return <ReviewStep state={state} />
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
          </DialogTitle>
          <DialogDescription>
            Build intelligent alerts with custom conditions and actions
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div
                key={step.id}
                className="flex flex-col items-center gap-2 flex-1"
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    currentStep > step.id
                      ? 'bg-green-500 text-white'
                      : currentStep === step.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    step.id
                  )}
                </div>
                <div className="text-center">
                  <div className="text-xs font-medium">{step.title}</div>
                  <div className="text-xs text-muted-foreground hidden sm:block">
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Progress value={(currentStep / STEPS.length) * 100} />
        </div>

        {/* Step Content */}
        <div className="py-6">{renderStep()}</div>

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            {currentStep < STEPS.length ? (
              <Button onClick={handleNext} disabled={!canProceed()}>
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving || !canProceed()}>
                {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
